/**
 * Application-wide constants
 * Centralized place for magic numbers and configuration values
 */

// Szállítás: nincs itt beégetett ár/küszöb — a hivatalos árlista az adatbázis
// (/api/shipping/config/, /api/shipping-methods/; lásd hooks/useShippingConfig).

// Payment configuration
export const PAYMENT = {
  MIN_ORDER_AMOUNT: 1000, // Minimum order amount (HUF)
  MAX_ORDER_AMOUNT: 1000000, // Maximum order amount (HUF)
} as const;

// Cart configuration
export const CART = {
  MAX_QUANTITY_PER_ITEM: 99, // Maximum quantity per product
  SESSION_STORAGE_KEY: 'guest_cart', // LocalStorage key for guest cart
  SYNC_DEBOUNCE_MS: 500, // Debounce time for cart sync (ms)
} as const;

// Product configuration
export const PRODUCT = {
  MAX_IMAGES: 10, // Maximum number of images per product
  IMAGE_MAX_SIZE_MB: 5, // Maximum image size (MB)
  THUMBNAIL_SIZE: 300, // Thumbnail size (px)
  FEATURED_PRODUCTS_COUNT: 6, // Number of featured products on homepage
  RECENT_PRODUCTS_COUNT: 10, // Number of recently viewed products to keep
} as const;

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20, // Default items per page
  PRODUCTS_PER_PAGE: 20, // Products per page
  ORDERS_PER_PAGE: 10, // Orders per page
} as const;

// UI/UX
export const UI = {
  TOAST_DURATION: 3000, // Toast notification duration (ms)
  DEBOUNCE_SEARCH_MS: 300, // Search input debounce (ms)
  MIN_SEARCH_LENGTH: 2, // Minimum characters for search
  MOBILE_BREAKPOINT: 768, // Mobile breakpoint (px)
  TABLET_BREAKPOINT: 1024, // Tablet breakpoint (px)
} as const;

// API
export const API = {
  TIMEOUT_MS: 30000, // API request timeout (ms)
  RETRY_ATTEMPTS: 3, // Number of retry attempts for failed requests
  RETRY_DELAY_MS: 1000, // Delay between retries (ms)
} as const;

// Validation
export const VALIDATION = {
  PASSWORD_MIN_LENGTH: 8, // Minimum password length
  PHONE_LENGTH: 11, // Expected phone number length (+36XXXXXXXXX)
  TAX_NUMBER_LENGTH: 13, // Hungarian tax number length (XXXXXXXX-X-XX)
  POSTAL_CODE_LENGTH: 4, // Hungarian postal code length
} as const;

// Design Editor
export const DESIGN_EDITOR = {
  CANVAS_WIDTH: 800, // Canvas width (px)
  CANVAS_HEIGHT: 600, // Canvas height (px)
  MAX_TEXT_LENGTH: 100, // Maximum text length for customization
  MIN_FONT_SIZE: 12, // Minimum font size (px)
  MAX_FONT_SIZE: 72, // Maximum font size (px)
  LOGO_MAX_SIZE_MB: 2, // Maximum logo file size (MB)
} as const;

// Newsletter
export const NEWSLETTER = {
  POPUP_DELAY_MS: 10000, // Newsletter popup delay (ms)
  COOKIE_EXPIRY_DAYS: 30, // Newsletter cookie expiry (days)
} as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_DATA: 'user_data',
  GUEST_CART: 'guest_cart',
  RECENT_PRODUCTS: 'recent_products',
  WISHLIST: 'wishlist',
  COOKIE_CONSENT: 'cookie_consent',
  NEWSLETTER_SHOWN: 'newsletter_popup_shown',
} as const;

// Order statuses
export const ORDER_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  PROCESSING: 'processing',
  SHIPPED: 'shipped',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
} as const;

// Payment statuses
export const PAYMENT_STATUS = {
  PENDING: 'pending',
  PAID: 'paid',
  FAILED: 'failed',
  REFUNDED: 'refunded',
} as const;

// Payment methods
export const PAYMENT_METHODS = {
  CARD: 'card',
  BANK_TRANSFER: 'bank_transfer',
  CASH_ON_DELIVERY: 'cash_on_delivery',
} as const;

// Product types
export const PRODUCT_TYPES = {
  SIMPLE: 'simple',
  VARIABLE: 'variable',
  CUSTOM: 'custom',
} as const;

// Error messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Hálózati hiba történt. Kérjük, próbáld újra később!',
  SERVER_ERROR: 'Szerver hiba történt. Kérjük, próbáld újra később!',
  UNAUTHORIZED: 'Nincs jogosultságod ehhez a művelethez.',
  NOT_FOUND: 'A keresett tartalom nem található.',
  VALIDATION_ERROR: 'Kérjük, ellenőrizd az adatokat!',
  TIMEOUT_ERROR: 'A kérés időtúllépés miatt megszakadt.',
} as const;

// Success messages
export const SUCCESS_MESSAGES = {
  CART_ADD: 'Termék hozzáadva a kosárhoz!',
  CART_UPDATE: 'Kosár frissítve!',
  CART_REMOVE: 'Termék eltávolítva a kosárból!',
  ORDER_CREATED: 'Rendelés sikeres!',
  PROFILE_UPDATED: 'Profil frissítve!',
  NEWSLETTER_SUBSCRIBED: 'Feliratkozás sikeres!',
  WISHLIST_ADD: 'Termék hozzáadva a kívánságlistához!',
  WISHLIST_REMOVE: 'Termék eltávolítva a kívánságlistából!',
} as const;
